<template>
  <v-row
    id="avatar-wrapper"
    no-gutters
    :style="{ height: height + 'px', width: width + 'px' }"
  >
    <v-col
      id="divVHSS"
      align="center"
      justify="center"
    >
    </v-col>

    <div id="avatar-accessories">
      <div
        v-if="width > 600"
        style="width: 650px; margin-left: 170px;"
      >
        <v-row no-gutters>
          <v-col
            cols="12"
            align="center"
            justify="center"
          >
            <v-card
              class="pa-2 cardColor d-flex justify-center"
              height="175px"
              width="550px"
            >
              <v-img
                src="@/assets/logo.png"
                width="450"
                contain
              />
            </v-card>
          </v-col>
        </v-row>
      </div>
    </div>
  </v-row>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "avatar",

  props: {
    initialWidth: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      width: this.initialWidth,
      count: 0,
      currentText: "",
      speechRequestId: 0,
    };
  },

  computed: {
    ...mapGetters([
      "getCurrentLanguage",
      "getAvatarSpeech",
      "getIsSpeaking",
      "getDefaultBubbleText",
      "getenableADA",
      "getAvatarLangCodes",
      "getAvatarDetails",
      "getKioskId",
    ]),

    height: {
      get() {
        if (this.$route.path === "/" || this.$route.path === "/loadData") {
          return this.width - 65;
        }

        return this.width - 15;
      },

      set(newVal) {
        this.width = newVal;
      },
    },

    spVoice() {
      return this.getAvatarLangCodes.voiceId;
    },

    spLang() {
      return this.getAvatarLangCodes.langId;
    },

    spEngine() {
      return this.getAvatarLangCodes.engineId;
    },
  },

  watch: {
    width() {
      if (typeof window.dynamicResize === "function") {
        window.dynamicResize(this.width, this.height);
      }
    },

    getAvatarSpeech(newVal) {
      const speechText = String(newVal || "").trim();

      if (!speechText) {
        //this.stopAvatarSpeech();
        return;
      }

      this.speak(speechText);
    },
  },

  methods: {
    stopAvatarSpeech() {
      /*
       * Incrementing this number invalidates any old sayText() promises.
       * This prevents an old request from restarting speech after stop is called.
       */
      this.speechRequestId += 1;

      try {
        if (typeof window.stopSpeech === "function") {
          window.stopSpeech();
        }
      } catch (error) {
        console.log("Error stopping avatar speech:", error.message);
      }

      /*
       * This does nothing to SitePal speech, but safely stops native browser
       * speech if any browser speech has been started elsewhere in the app.
       */
      try {
        if ("speechSynthesis" in window) {
          window.speechSynthesis.cancel();
        }
      } catch (error) {
        console.log("Error cancelling browser speech:", error.message);
      }

      this.currentText = "";
      this.$store.commit("setIsSpeaking", false);

      // Stopping speech should also drop anything still queued and resolve
      // its promise, otherwise a manual stop would leave any `await`ing
      // caller (and the queue itself) waiting on a talkEnded that will never
      // come.
      this.$store.dispatch("clearSpeechQueue");
    },

    async speak(speakText) {
      const text = String(speakText || "").trim();

      if (!text) {
        return;
      }

      /*
       * Stop the old speech first. This also invalidates old pending promises.
       */
      this.stopAvatarSpeech();

      /*
       * Save the current request ID. If stopAvatarSpeech() runs later,
       * speechRequestId changes and this request becomes inactive.
       */
      const currentRequestId = this.speechRequestId;

      this.currentText = text;

      if (typeof window.sayText !== "function") {
        console.log("Avatar sayText function is not available.");
        return;
      }

      try {
        const speechResponse = window.sayText(
          text,
          this.spVoice,
          this.spLang,
          this.spEngine
        );

        /*
         * Do NOT call window.sayText() again inside .then().
         * That was causing speech to restart after window.stopSpeech().
         */
        if (
          speechResponse &&
          typeof speechResponse.then === "function"
        ) {
          speechResponse
            .then(response => {
              /*
               * Ignore a response from speech that was stopped or replaced.
               */
              if (currentRequestId !== this.speechRequestId) {
                return;
              }

              /*
               * Keep your existing Strapi speech-start logging behavior,
               * but do not speak a second time.
               */
              if (response && response.status === 1) {
                this.$store.dispatch("addSpeakDatatoStrapi", {
                  outputSpeech: text,
                  speechCompletion: false,
                  timeStamp: new Date(),
                  kioskId: this.getKioskId,
                });
              }
            })
            .catch(error => {
              if (currentRequestId !== this.speechRequestId) {
                return;
              }

              console.log("Error at Speak Text:", error.message);
              this.$store.commit("setIsSpeaking", false);
            });
        }
      } catch (error) {
        console.log("Error at Speak Text:", error.message);
        this.$store.commit("setIsSpeaking", false);
      }
    },

    forceResize() {
      if (typeof window.dynamicResize === "function") {
        window.dynamicResize(this.width, this.height);
      }
    },

    initiateLogout(value) {
      if (this.count === 0) {
        setTimeout(() => {
          this.count = 0;
        }, 40000);
      }

      if (value === "1") {
        this.count = 1;
        return;
      }

      if (this.count >= 1) {
        this.count += 1;

        if (this.count === 7) {
          this.count = 0;
          this.$store.dispatch("logout");
        }
      }
    },
  },

  mounted() {
    /*
     * Use this instead of window.stopSpeech() everywhere else in the app.
     *
     * window.stopAvatarSpeech() stops the avatar AND invalidates pending
     * sayText promises, preventing old speech from starting again.
     */
    window.stopAvatarSpeech = () => {
      this.stopAvatarSpeech();
    };

    window.vh_sceneLoaded = () => {
      this.forceResize();

      if (typeof window.setIdleMovement === "function") {
        window.setIdleMovement(0);
      }

      this.$store.commit("setAvatarLoaded", true);
    };

    window.vh_audioStarted = () => {
      this.$store.commit("setIsSpeaking", true);
      this.$store.commit("setIsThinking", false);
    };

    window.vh_audioProgress = percentPlayed => {
      console.log("Audio Progress:", percentPlayed);
    };

    window.vh_talkEnded = () => {
      /*
       * When speech was deliberately stopped, currentText is blank.
       * Do not record a completion event for cancelled speech.
       */
      if (!this.currentText) {
        this.$store.commit("setIsSpeaking", false);
        this.$store.dispatch("completeCurrentSpeech");
        return;
      }

      this.$store.dispatch("addSpeakDatatoStrapi", {
        outputSpeech: this.currentText,
        speechCompletion: true,
        timeStamp: new Date(),
        kioskId: this.getKioskId,
      });

      this.$store.commit("setIsSpeaking", false);
      this.$store.dispatch("completeCurrentSpeech");
    };
  },

  beforeDestroy() {
    this.stopAvatarSpeech();

    if (window.stopAvatarSpeech) {
      delete window.stopAvatarSpeech;
    }
  },
};
</script>

<style>
#divVHSS {
  width: 100%;
  z-index: 0 !important;
}

#divVHSS-inner {
  left: -10px !important;
  bottom: 0px !important;
}

.character,
.vhss_main_container,
._html5Player {
  margin-top: -10px !important;
  z-index: -1 !important;
  position: initial !important;
}

#avatar-accessories {
  z-index: 1;
  height: 83%;
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  position: absolute;
  padding-bottom: 20px;
}

.cardColor {
  background-color: white !important;
  opacity: 0.9 !important;
  border-radius: 30px !important;
}
</style>

<i18n>
{
  "en": {
    "buubletextafteraction": "Tap the microphone button, to ask me a question."
  },
  "es": {
    "buubletextafteraction": "El reconocimiento de voz está desactivado para el idioma español."
  }
}
</i18n>